import React from "react";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { formatIsoDateTime, formatIsoDuration } from "utils/dateUtils";
import CustomScrollbars from "../CustomScrollbars";
import RunActionsMenu, { type RunActionHandlers } from "./RunActionsMenu";
import RunClientStatus from "./RunClientStatus";
import RunStatusChip from "./RunStatusChip";
import RunTimestamp from "./RunTimestamp";
import TableSkeletonRows from "./TableSkeletonRows";
import { HEADER_CELL_SX, NO_WRAP_CELL_SX } from "./tableStyles";
import { rerunAwaitsConfirmation } from "features/integrationsBeta/clientRunStatus";
import type {
  IntegrationRunListItem,
  IntegrationRunSortField,
} from "features/integrationsBeta/integrationsBeta";
import {
  timeHeaderSuffix,
  type TimezoneDisplayMode,
} from "features/integrationsBeta/timezoneDisplay";

const COLUMN_COUNT = 7;

type Props = {
  runs: IntegrationRunListItem[];
  isLoading: boolean;
  isFetching: boolean;
  selectedRunId?: number | null;
  onSelectRun: (run: IntegrationRunListItem) => void;
  sortBy?: IntegrationRunSortField;
  sortOrder: "asc" | "desc";
  onSortChange: (field: IntegrationRunSortField) => void;
  statusLabel: (value: string | null | undefined) => string;
  triggerLabel: (value: string | null | undefined) => string;
  clientStatusLabel: (value: string | null | undefined) => string;
  timezoneMode: TimezoneDisplayMode;
  displayZone?: string;
  /** Manage: Set client status in the row menu. */
  canEdit: boolean;
  /** Contribute: Re-run in the row menu. */
  canRun: boolean;
  actions: RunActionHandlers;
  pagination?: React.ReactNode;
};

/**
 * Tooltip of the client-status pill: who set it, both notes (each labelled
 * with who reads it), the re-run state.
 */
function clientStatusHint(
  run: IntegrationRunListItem,
  statusLabel: Props["statusLabel"],
  displayZone?: string,
): React.ReactNode {
  const lines: string[] = [];
  if (run.client_status_override !== null) {
    const when = run.client_status_updated_at
      ? ` on ${formatIsoDateTime(run.client_status_updated_at, displayZone)}`
      : "";
    lines.push(
      `Set manually by ${run.client_status_updated_by || "Calibrate"}${when}`,
    );
  } else {
    lines.push("Automatic");
  }
  if (run.client_note) lines.push(`Note for the client: ${run.client_note}`);
  if (run.internal_note) lines.push(`Internal note: ${run.internal_note}`);
  if (run.latest_rerun_id !== null) {
    lines.push(`Latest re-run: ${statusLabel(run.latest_rerun_status)}`);
    if (rerunAwaitsConfirmation(run)) {
      lines.push("Confirm the client status once you have checked the result");
    }
  }
  return (
    <Box>
      {lines.map((line) => (
        <Typography key={line} variant="caption" display="block">
          {line}
        </Typography>
      ))}
    </Box>
  );
}

export default function RunsHistoryTable({
  runs,
  isLoading,
  isFetching,
  selectedRunId,
  onSelectRun,
  sortBy,
  sortOrder,
  onSortChange,
  statusLabel,
  triggerLabel,
  clientStatusLabel,
  timezoneMode,
  displayZone,
  canEdit,
  canRun,
  actions,
  pagination,
}: Props) {
  const sortHeader = (field: IntegrationRunSortField, label: string) => (
    <TableCell sx={HEADER_CELL_SX}>
      <TableSortLabel
        active={sortBy === field}
        direction={sortBy === field ? sortOrder : "asc"}
        onClick={() => onSortChange(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const timeSuffix = timeHeaderSuffix(timezoneMode);
  const timeLabel = (label: string) =>
    timeSuffix ? `${label} · ${timeSuffix}` : label;
  const showCellAbbreviation = timezoneMode === "client";

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 260,
        overflow: "clip",
      }}
    >
      <CustomScrollbars
        sx={{ flex: 1, minWidth: 0, overflowX: "auto" }}
        trackOffsetTop={40}
      >
        <Table
          stickyHeader
          size="small"
          sx={{ minWidth: 900, borderColor: "divider" }}
        >
          <TableHead>
            <TableRow>
              {sortHeader("started_at", timeLabel("Started"))}
              {sortHeader("finished_at", timeLabel("Finished"))}
              {sortHeader("duration", "Duration")}
              {sortHeader("trigger_type", "Trigger")}
              {sortHeader("status", "Status")}
              {sortHeader("client_status", "Client status")}
              <TableCell sx={{ width: 48, p: 0.5 }} aria-label="Actions" />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableSkeletonRows columns={COLUMN_COUNT} />
            ) : runs.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT}
                  sx={{ py: 4, borderBottom: "none" }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No runs yet
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              runs.map((run) => {
                const isSelected = selectedRunId === run.id;
                const originalRunId = run.original_run;
                return (
                  <TableRow
                    key={run.id}
                    selected={isSelected}
                    onClick={() => onSelectRun(run)}
                    sx={{
                      cursor: "pointer",
                      opacity: isFetching ? 0.6 : 1,
                      "&:last-child td": { borderBottom: "none" },
                      "&:hover": {
                        bgcolor: "calibrate.primarySoft06",
                      },
                    }}
                  >
                    <TableCell sx={NO_WRAP_CELL_SX}>
                      <RunTimestamp
                        iso={run.started_at}
                        timeZone={displayZone}
                        showAbbreviation={showCellAbbreviation}
                      />
                    </TableCell>
                    <TableCell sx={NO_WRAP_CELL_SX}>
                      {run.finished_at ? (
                        <RunTimestamp
                          iso={run.finished_at}
                          timeZone={displayZone}
                          showAbbreviation={showCellAbbreviation}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell sx={NO_WRAP_CELL_SX}>
                      {formatIsoDuration(run.started_at, run.finished_at)}
                    </TableCell>
                    <TableCell sx={NO_WRAP_CELL_SX}>
                      <Box
                        sx={{ display: "inline-flex", alignItems: "center" }}
                      >
                        <Typography variant="body2">
                          {triggerLabel(run.trigger_type)}
                        </Typography>
                        {originalRunId !== null && (
                          <Tooltip title="Re-run of an earlier run - open it">
                            <IconButton
                              size="small"
                              aria-label="Open the original run"
                              sx={{ ml: 0.5, p: 0.25 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                actions.onOpenRun(originalRunId);
                              }}
                            >
                              <ReplayIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <RunStatusChip
                        status={run.status}
                        label={statusLabel(run.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <RunClientStatus
                        run={run}
                        clientStatusLabel={clientStatusLabel}
                        tooltip={clientStatusHint(
                          run,
                          statusLabel,
                          displayZone,
                        )}
                      />
                    </TableCell>
                    {/* The menu is a React child of this cell, so stopping
                        propagation here keeps menu clicks (and the backdrop
                        click that closes it) from selecting the row. */}
                    <TableCell
                      align="right"
                      sx={{ width: 48, p: 0.5 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <RunActionsMenu
                        run={run}
                        canEdit={canEdit}
                        canRun={canRun}
                        statusLabel={statusLabel}
                        {...actions}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CustomScrollbars>
      {pagination}
    </Paper>
  );
}
